import * as _ from "underscore";

import * as iOSBridge from "iOSBridge";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import type {
  DictionaryPaneState,
  SmartDictionaryPaneState,
} from "views/ReaderSidebarPanes";
import * as walkthrough from "walkthrough";

interface DictionaryManagerDeps {
  stores: any;
  sideBarWidth: number;
  getSideBarVisible: () => boolean;
  getSelectedTabName: () => string | undefined;
  selectSidebarTab: (tabName: string) => Promise<void> | void;
  toggleSidebar: (retainPosition?: number) => Promise<void>;
  updateDictionaryPaneState: (state: DictionaryPaneState) => void;
  updateLayout: (span?: any, doNotRemoveFirstLine?: boolean) => void;
  updateSmartDictionaryPaneState: (state: SmartDictionaryPaneState) => void;
  isOwner: () => boolean;
  /** The text's position of the word just looked up */
  onWordLookedUp: (position: number) => void;
  /** On phones, the native dictionary's: the reader's sheet below its tabs */
  getDictionarySheetHeight: () => number | undefined;
}

/**
 * The iOS app shows the dictionary in a native sheet, where sites that forbid
 * being framed still load
 */
const openNativeDictionary = () => iOSBridge.get()?.openDictionary;

export function createDictionaryManager(deps: DictionaryManagerDeps) {
  const { stores, getSideBarVisible } = deps;

  /**
   * This is set when you click on a highlighted word (your word) so that
   * when the sidebar is opened to the explain tab, it will have a context
   * event though the context from the current text may not have been added
   * to the UserWord
   */
  let previousNewContext: { text: string } | undefined;
  let currentDictionaryHtml: string | undefined;
  let currentDictionaryURL: string | undefined;
  let currentFallbackText: string | undefined;
  let previousUserWord: any;
  let oldSmartDictionaryProps: any;
  let externalDictionaryWindow: Window | null;
  let disposeRemoteUserWordsChange: (() => void) | undefined;

  const dictionaryVisible = () => {
    if (getSideBarVisible() && deps.getSelectedTabName() === "dictionary") {
      return true;
    } else {
      return false;
    }
  };

  const smartDictionaryVisible = () => {
    if (
      getSideBarVisible() &&
      deps.getSelectedTabName() === "smartDictionary"
    ) {
      return true;
    } else {
      return false;
    }
  };

  const showDictionaryIfVisible = () => {
    if (!dictionaryVisible()) {
      return;
    }

    const openNative = openNativeDictionary();
    deps.updateDictionaryPaneState({
      fallbackText: currentFallbackText,
      html: currentDictionaryHtml,
      native: Boolean(openNative),
      url: currentDictionaryURL,
    });
    if (openNative && currentDictionaryURL) {
      openNative(currentDictionaryURL, deps.getDictionarySheetHeight());
    }

    if (currentDictionaryHtml) {
      walkthrough.setAchievement("readerSidebar", true);
    }
  };

  const monolingualLookup = (word: string, span?: any) => {
    const embeddedDictionary = languageMap.getEmbeddedDictionary(
      stores.bookModel.book.value.language,
      stores.bookModel.book.value.language,
      word,
      // XXX populate context
      undefined,
      stores.user
    );

    currentDictionaryHtml =
      (embeddedDictionary && embeddedDictionary.html) ||
      "<p>Sorry, monolingual dictionary not available for " +
        languageMap.map[stores.bookModel.book.value.language].english +
        ".</p>";
    currentDictionaryURL = embeddedDictionary && embeddedDictionary.URL;

    if (stores.user.state.value.readerMonolingualAutoOpen === "off") {
      showDictionaryIfVisible();
      return;
    }

    // TODO: external dictionary window
    if (!getSideBarVisible()) {
      void deps.toggleSidebar(span);
    } else {
      deps.updateLayout(span, false);
    }

    if (deps.getSelectedTabName() === "smartDictionary") {
      deps.selectSidebarTab("smartDictionary");
    } else {
      deps.selectSidebarTab("dictionary");
    }
  };

  /**
   * Returns true if shown, false if not.
   */
  const showSmartDictionaryIfAppropriate = async (newContext?: {
    text: string;
  }) => {
    if (!smartDictionaryVisible()) {
      return false;
    }

    if (await readlangClient.isLocalMode()) {
      oldSmartDictionaryProps = undefined;
      deps.updateSmartDictionaryPaneState({ mode: "local" });
      return false;
    }

    const userWord = previousUserWord;
    if (!userWord) {
      oldSmartDictionaryProps = undefined;
      deps.updateSmartDictionaryPaneState({ mode: "empty" });
      return false;
    }

    const currentContext =
      newContext ??
      _.last(
        (userWord.state.value.contexts ?? []).filter(
          (context) =>
            context.bookID &&
            (context.bookID === stores.bookModel.id.toString() ||
              (context.bookID._id &&
                context.bookID._id.toString() ===
                  stores.bookModel.id.toString()))
        )
      ) ??
      previousNewContext;

    const newSmartDictionaryProps = {
      userWord,
      context: `${currentContext ? currentContext.text : undefined}${
        stores.user.hasFeature("explainTitleAndAuthor")
          ? ` (from "${stores.bookModel.book.value.title}" ${
              stores.bookModel.book.value.author
                ? `by ${stores.bookModel.book.value.author}`
                : ""
            })`
          : ""
      }`,
      monolingualMode: stores.user.readerTranslations.value === "off",
    };
    // Only re-render the smart dictionary if the properties have changed
    if (!_.isEqual(oldSmartDictionaryProps, newSmartDictionaryProps)) {
      deps.updateSmartDictionaryPaneState({
        mode: "word",
        props: newSmartDictionaryProps,
      });
      oldSmartDictionaryProps = newSmartDictionaryProps;
    }

    // Update if translation was edited in the Words tab
    disposeRemoteUserWordsChange?.();
    disposeRemoteUserWordsChange = stores.remoteUserWords.onChange((words) => {
      const updatedWord = words.find(
        (word) => word.id === previousUserWord?.id
      );
      if (updatedWord) {
        onChangeTranslation(updatedWord);
      }
    });

    return true;
  };

  const onChangeTranslation = (model) => {
    if (model.id === previousUserWord?.id) {
      previousUserWord = model;
      showSmartDictionaryIfAppropriate();
    }
  };

  const updateDictionary = (innerOptions?: {
    userWord?: any;
    newContext?: { text: string };
    /** Of the word looked up in the text */
    position?: number;
  }) => {
    const userWord = innerOptions?.userWord ?? previousUserWord;

    if (!userWord) {
      return;
    }

    previousUserWord = userWord;
    if (innerOptions?.position !== undefined) {
      deps.onWordLookedUp(innerOptions.position);
    }

    if (
      userWord.state.value.language !== stores.bookModel.book.value.language
    ) {
      userWord.save(
        { language: stores.bookModel.book.value.language },
        { patch: true }
      );
    }

    showSmartDictionaryIfAppropriate(innerOptions?.newContext);

    if (stores.user.readerTranslations.value === "off") {
      monolingualLookup(userWord.state.value.word /* TODO: add spanToRetain */);
      return;
    }

    const embeddedDictionary = languageMap.getEmbeddedDictionary(
      userWord.state.value.language,
      stores.user.firstLanguage.value,
      userWord.state.value.word,
      // XXX Is the last context guaranteed to be the one we want?
      _.last(userWord.state.value.contexts)?.text ?? previousNewContext?.text,
      stores.user
    );

    currentDictionaryHtml =
      embeddedDictionary.html ||
      "<p>Sorry, we don't have a dictionary between your two languages</p>";
    currentDictionaryURL = embeddedDictionary.URL;

    if (
      embeddedDictionary.target &&
      embeddedDictionary.target !== stores.user.firstLanguage.value
    ) {
      currentFallbackText =
        "(Fallback to " +
        languageMap.map[embeddedDictionary.target].english +
        " below)";
    } else {
      currentFallbackText = undefined;
    }

    if (externalDictionaryWindow && !externalDictionaryWindow.closed) {
      externalDictionaryWindow.location.href = embeddedDictionary.URL;
    }

    showDictionaryIfVisible();
  };

  const cleanup = () => {
    currentDictionaryHtml = undefined;
    currentDictionaryURL = undefined;
    currentFallbackText = undefined;
    previousUserWord = undefined;
    oldSmartDictionaryProps = undefined;
  };

  const updateDictionarySize = () => {
    showDictionaryIfVisible();
  };

  const openExternalDictionary = () => {
    if (!currentDictionaryURL) {
      return;
    }
    const openNative = openNativeDictionary();
    if (openNative) {
      openNative(currentDictionaryURL, deps.getDictionarySheetHeight());
      return;
    }
    externalDictionaryWindow = window.open(
      currentDictionaryURL,
      "_blank",
      `location=no,menubar=no,toolbar=no,scrollbars=yes,width=${deps.sideBarWidth},height=550`
    );
  };

  return {
    monolingualLookup,
    showSmartDictionaryIfAppropriate,
    updateDictionary,
    showDictionaryIfVisible,
    dictionaryVisible,
    smartDictionaryVisible,
    updateDictionarySize,
    openExternalDictionary,
    cleanup,
    getPreviousUserWord: () => previousUserWord,
    setPreviousUserWord: (userWord: any) => {
      previousUserWord = userWord;
    },
    setPreviousNewContext: (context: { text: string }) => {
      previousNewContext = context;
    },
  };
}

export type ReaderPageDictionaryManager = ReturnType<
  typeof createDictionaryManager
>;
